import React, { Component, RefObject } from 'react';
import { 
  Send, 
  Paperclip, 
  X, 
  Reply,
  Download,
  Users,
  UserPlus,
  Crown,
  Shield,
  MapPin,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Loader,
  Forward
} from 'lucide-react';
import styles from './GroupChat.module.scss';
import { apiService } from '@/services/api';
import { groupChatWebSocketService } from '@/services/groupChatWebSocket';
import ForwardDestinationModal from '@/components/Friends/ForwardDestinationModal';

// Attachment interface matching Chat component
interface GroupMessageAttachment {
  id: string;
  attachmentType: 'image' | 'video' | 'file' | 'sticker' | 'youtube' | 'facebook' | 'twitter' | 'instagram' | 'link' | 'location';
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
  thumbnailUrl?: string;
  linkUrl?: string;
  metadata?: {
    videoId?: string;
    embedUrl?: string;
    postId?: string;
    username?: string;
    tweetId?: string;
    latitude?: number;
    longitude?: number;
    address?: string;
    placeName?: string;
    [key: string]: any;
  };
  createdAt: string;
}

// Comment interface
interface GroupMessageComment {
  id: string;
  messageId: string;
  commenterId: string;
  commentText: string;
  replyToComment?: {
    id: string;
    commentText: string;
    commenterId: string;
    commenterName: string;
    createdAt: string;
  };
  attachments?: GroupMessageAttachment[];
  positiveSentimentsCount: number;
  negativeSentimentsCount: number;
  userSentiment: 'positive' | 'negative' | null;
  createdAt: string;
  updatedAt: string;
  commenter?: {
    id: string;
    userId: string;
    displayName: string;
    profilePic?: string;
  };
}

// Group Message interface
interface GroupMessage {
  id: string;
  senderId: string;
  groupId: string;
  messageText: string;
  replyToMessage?: {
    id: string;
    messageText: string;
    senderId: string;
    senderName: string;
    senderProfilePic?: string;
    createdAt: string;
  };
  attachments?: GroupMessageAttachment[];
  commentsCount: number;
  positiveSentimentsCount: number;
  negativeSentimentsCount: number;
  createdAt: string;
  updatedAt: string;
  sender?: {
    id: string;
    userId: string;
    displayName: string;
    profilePic?: string;
    role?: 'admin' | 'moderator' | 'member';
  };
}

interface GroupChatProps {
  group: {
    id: string;
    name: string;
    description?: string;
    profilePicUrl?: string;
  };
  currentUserId: string;
  currentUserProfileId: string;
  currentUserRole: 'admin' | 'moderator' | 'member';
  onClose: () => void;
  onSendMessage?: (
    groupId: string, 
    messageText: string, 
    replyToMessageId?: string, 
    attachments?: File[],
    location?: {
      latitude: number;
      longitude: number;
      address?: string;
      placeName?: string;
    }
  ) => Promise<void>;
  onLoadMessages?: (groupId: string) => Promise<GroupMessage[]>;
  onNewMessage?: (message: GroupMessage) => void;
  onTyping?: (isTyping: boolean) => void;
  onAddMembers?: () => void;
  members?: Array<{
    id: string;
    userId: string;
    displayName: string;
    profilePic?: string;
    role?: 'admin' | 'moderator' | 'member';
  }>;
}

interface SelectedFile {
  file: File;
  preview?: string;
}

interface GroupChatState {
  messages: GroupMessage[];
  comments: Record<string, GroupMessageComment[]>; // messageId -> comments[]
  expandedComments: Set<string>; // messageIds with expanded comments
  inputMessage: string;
  replyingTo: GroupMessage | null;
  selectedFiles: SelectedFile[];
  filePreviews: SelectedFile[]; // File previews for selected files
  location: {
    latitude: number;
    longitude: number;
    address?: string;
    placeName?: string;
  } | null;
  isGettingLocation: boolean;
  loading: boolean;
  isUploading: boolean;
  typing: boolean;
  typingTimeout: NodeJS.Timeout | null;
  commentInputs: Record<string, string>; // messageId -> comment text
  commentFiles: Record<string, SelectedFile[]>; // messageId -> selected files
  commentLocations: Record<string, { latitude: number; longitude: number; address?: string; placeName?: string } | null>; // messageId -> location
  isUploadingComment: Record<string, boolean>; // messageId -> isUploading
  forwardMessage: GroupMessage | null;
}

class GroupChat extends Component<GroupChatProps, GroupChatState> {
  private chatContainerRef: RefObject<HTMLDivElement> = React.createRef();
  private fileInputRef: RefObject<HTMLInputElement> = React.createRef();
  private commentFileInputs: Record<string, RefObject<HTMLInputElement>> = {};

  constructor(props: GroupChatProps) {
    super(props);
    this.state = {
      messages: [],
      comments: {},
      expandedComments: new Set(),
      inputMessage: '',
      replyingTo: null,
      selectedFiles: [],
      filePreviews: [],
      location: null,
      isGettingLocation: false,
      loading: false,
      isUploading: false,
      typing: false,
      typingTimeout: null,
      commentInputs: {},
      commentFiles: {},
      commentLocations: {},
      isUploadingComment: {},
      forwardMessage: null
    };
  }

  componentDidMount() {
    this.loadMessages();
    this.setupSocketListeners();
    
    // Ensure socket is connected before joining room
    if (!groupChatWebSocketService.isConnected()) {
      groupChatWebSocketService.connect();
    }
    
    // Wait a bit for connection, then join room
    const joinRoom = () => {
      if (groupChatWebSocketService.isConnected()) {
        groupChatWebSocketService.joinGroupRoom(this.props.group.id);
        console.log('[GroupChat] Joined group room:', this.props.group.id);
      } else {
        // Retry after a short delay
        setTimeout(joinRoom, 100);
      }
    };
    
    // Start joining room
    if (groupChatWebSocketService.isConnected()) {
      joinRoom();
    } else {
      // Wait for connection
      groupChatWebSocketService.on('connect', () => {
        joinRoom();
      });
      setTimeout(joinRoom, 500); // Fallback timeout
    }
  }

  componentWillUnmount() {
    groupChatWebSocketService.leaveGroupRoom(this.props.group.id);
    this.cleanupSocketListeners();
  }

  componentDidUpdate(prevProps: GroupChatProps, prevState: GroupChatState) {
    if (prevProps.group.id !== this.props.group.id) {
      groupChatWebSocketService.leaveGroupRoom(prevProps.group.id);
      this.loadMessages();
      groupChatWebSocketService.joinGroupRoom(this.props.group.id);
    }
    
    // Scroll to bottom when messages finish loading (after initial load completes)
    if (prevState.loading && !this.state.loading && this.state.messages.length > 0) {
      // Initial load complete - scroll to bottom with multiple attempts
      requestAnimationFrame(() => {
        this.scrollToBottom();
        setTimeout(() => this.scrollToBottom(), 100);
        setTimeout(() => this.scrollToBottom(), 300);
      });
    }
  }

  setupSocketListeners = () => {
    groupChatWebSocketService.on('group:message:new', this.handleNewMessage);
    groupChatWebSocketService.on('group:comment:new', this.handleNewComment);
    groupChatWebSocketService.on('group:comment:sentiment:updated', this.handleCommentSentimentUpdated);
    groupChatWebSocketService.on('group:comment:sentiment:removed', this.handleCommentSentimentRemoved);
  };

  cleanupSocketListeners = () => {
    groupChatWebSocketService.off('group:message:new', this.handleNewMessage);
    groupChatWebSocketService.off('group:comment:new', this.handleNewComment);
    groupChatWebSocketService.off('group:comment:sentiment:updated', this.handleCommentSentimentUpdated);
    groupChatWebSocketService.off('group:comment:sentiment:removed', this.handleCommentSentimentRemoved);
  };

  handleNewMessage = (data: { groupId: string; message: GroupMessage }) => {
    if (data.groupId === this.props.group.id) {
      const message = data.message;
      
      // Check if this is a reply message
      if (message.replyToMessage) {
        console.log('[GroupChat] Received reply message:', {
          replyId: message.id,
          originalMessageId: message.replyToMessage.id,
          originalSender: message.replyToMessage.senderName,
          originalText: message.replyToMessage.messageText
        });
        
        // Add message with reply preview
        this.addMessage(message);
        
        // Optionally scroll to or highlight the original message after a short delay
        // This gives time for the reply message to render first
        setTimeout(() => {
          const originalMessageElement = document.getElementById(`message-${message.replyToMessage!.id}`);
          if (originalMessageElement) {
            // Briefly highlight the original message
            originalMessageElement.style.transition = 'background-color 0.3s';
            originalMessageElement.style.backgroundColor = '#fef3c7';
            setTimeout(() => {
              originalMessageElement.style.backgroundColor = '';
            }, 2000);
          }
        }, 300);
      } else {
        // Regular message (not a reply)
        this.addMessage(message);
      }
    }
  };

  handleReplyClick = (message: GroupMessage) => {
    this.setState({ replyingTo: message });
    // Scroll to input area
    if (this.chatContainerRef.current) {
      setTimeout(() => {
        this.chatContainerRef.current?.scrollTo({
          top: this.chatContainerRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }, 100);
    }
  };

  handleCancelReply = () => {
    this.setState({ replyingTo: null });
  };

  handleReplyPreviewClick = (messageId: string) => {
    // Scroll to the original message
    const messageElement = document.getElementById(`message-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Highlight the message briefly
      messageElement.style.backgroundColor = '#fef3c7';
      setTimeout(() => {
        messageElement.style.backgroundColor = '';
      }, 2000);
    }
  };

  handleNewComment = (data: { groupId: string; messageId: string; comment: GroupMessageComment }) => {
    if (data.groupId === this.props.group.id) {
      this.addComment(data.messageId, data.comment);
    }
  };

  handleCommentSentimentUpdated = (data: { groupId: string; messageId: string; commentId: string; sentiment: 'positive' | 'negative'; customerId: string }) => {
    if (data.groupId === this.props.group.id) {
      this.updateCommentSentiment(data.messageId, data.commentId, data.sentiment, data.customerId);
    }
  };

  handleCommentSentimentRemoved = (data: { groupId: string; messageId: string; commentId: string; customerId: string }) => {
    if (data.groupId === this.props.group.id) {
      this.removeCommentSentiment(data.messageId, data.commentId, data.customerId);
    }
  };

  loadMessages = async () => {
    if (!this.props.onLoadMessages) {
      // Fallback to API if no prop provided
      try {
        this.setState({ loading: true });
        const response = await apiService.getGroupMessages(this.props.group.id);
        if (response.success && response.data) {
          const messages = response.data.messages || [];
          // Sort messages by createdAt (oldest first) to ensure proper order
          const sortedMessages = messages.sort((a: GroupMessage, b: GroupMessage) => {
            return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          });
          this.setState({ messages: sortedMessages }, () => {
            // Use requestAnimationFrame and multiple attempts to ensure scroll works
            requestAnimationFrame(() => {
              this.scrollToBottom();
              setTimeout(() => this.scrollToBottom(), 50);
              setTimeout(() => this.scrollToBottom(), 200);
            });
          });
        }
      } catch (error) {
        console.error('[GroupChat] Failed to load messages:', error);
      } finally {
        this.setState({ loading: false });
      }
      return;
    }

    this.setState({ loading: true });
    try {
      const messages = await this.props.onLoadMessages(this.props.group.id);
      // Sort messages by createdAt (oldest first) to ensure proper order
      const sortedMessages = (messages || []).sort((a: GroupMessage, b: GroupMessage) => {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      });
      this.setState({ messages: sortedMessages }, () => {
        // Use requestAnimationFrame and multiple attempts to ensure scroll works
        requestAnimationFrame(() => {
          this.scrollToBottom();
          setTimeout(() => this.scrollToBottom(), 50);
          setTimeout(() => this.scrollToBottom(), 200);
        });
      });
    } catch (error) {
      console.error('[GroupChat] Failed to load messages:', error);
    } finally {
      this.setState({ loading: false });
    }
  };

  loadComments = async (messageId: string) => {
    try {
      const response = await apiService.getGroupMessageComments(messageId);
      if (response.success && response.data && response.data.comments) {
        this.setState(prevState => ({
          comments: {
            ...prevState.comments,
            [messageId]: response.data!.comments || []
          }
        }));
      }
    } catch (error) {
      console.error('[GroupChat] Failed to load comments:', error);
    }
  };

  public addMessage = (message: GroupMessage) => {
    const messageExists = this.state.messages.some(m => m.id === message.id);
    
    if (!messageExists) {
      const optimisticMessageIndex = this.state.messages.findIndex(m => 
        m.id.startsWith('temp-') &&
        m.senderId === message.senderId &&
        m.groupId === message.groupId &&
        Math.abs(new Date(m.createdAt).getTime() - new Date(message.createdAt).getTime()) < 5000
      );

      if (optimisticMessageIndex !== -1) {
        this.setState(prevState => {
          const newMessages = [...prevState.messages];
          newMessages[optimisticMessageIndex] = message;
          return { 
            messages: newMessages,
            isUploading: false
          };
        }, () => setTimeout(() => this.scrollToBottom(), 50));
      } else {
        this.setState(prevState => ({
          messages: [...prevState.messages, message],
          isUploading: false
        }), () => setTimeout(() => this.scrollToBottom(), 50));
      }
    } else {
      if (this.state.isUploading) {
        this.setState({ isUploading: false });
      }
    }
  };

  addComment = (messageId: string, comment: GroupMessageComment) => {
    this.setState(prevState => {
      const existingComments = prevState.comments[messageId] || [];
      const commentExists = existingComments.some(c => c.id === comment.id);
      
      if (commentExists) {
        return prevState;
      }

      return {
        ...prevState,
        comments: {
          ...prevState.comments,
          [messageId]: [...existingComments, comment]
        },
        isUploadingComment: {
          ...prevState.isUploadingComment,
          [messageId]: false
        }
      };
    });

    // Update message comment count
    this.setState(prevState => ({
      messages: prevState.messages.map(msg => 
        msg.id === messageId 
          ? { ...msg, commentsCount: (msg.commentsCount || 0) + 1 }
          : msg
      )
    }));
  };

  updateCommentSentiment = (messageId: string, commentId: string, sentiment: 'positive' | 'negative', customerId: string) => {
    this.setState(prevState => {
      const comments = prevState.comments[messageId] || [];
      const updatedComments = comments.map(comment => {
        if (comment.id === commentId) {
          // If user already had a sentiment, adjust counts
          let newPositive = comment.positiveSentimentsCount || 0;
          let newNegative = comment.negativeSentimentsCount || 0;
          
          if (comment.userSentiment === 'positive') {
            newPositive = Math.max(0, newPositive - 1);
          } else if (comment.userSentiment === 'negative') {
            newNegative = Math.max(0, newNegative - 1);
          }
          
          if (sentiment === 'positive') {
            newPositive++;
          } else {
            newNegative++;
          }
          
          return {
            ...comment,
            positiveSentimentsCount: newPositive,
            negativeSentimentsCount: newNegative,
            userSentiment: customerId === this.props.currentUserProfileId ? sentiment : comment.userSentiment
          };
        }
        return comment;
      });

      return {
        comments: {
          ...prevState.comments,
          [messageId]: updatedComments
        }
      };
    });
  };

  removeCommentSentiment = (messageId: string, commentId: string, customerId: string) => {
    this.setState(prevState => {
      const comments = prevState.comments[messageId] || [];
      const updatedComments = comments.map(comment => {
        if (comment.id === commentId) {
          let newPositive = comment.positiveSentimentsCount || 0;
          let newNegative = comment.negativeSentimentsCount || 0;
          
          if (comment.userSentiment === 'positive') {
            newPositive = Math.max(0, newPositive - 1);
          } else if (comment.userSentiment === 'negative') {
            newNegative = Math.max(0, newNegative - 1);
          }
          
          return {
            ...comment,
            positiveSentimentsCount: newPositive,
            negativeSentimentsCount: newNegative,
            userSentiment: customerId === this.props.currentUserProfileId ? null : comment.userSentiment
          };
        }
        return comment;
      });

      return {
        comments: {
          ...prevState.comments,
          [messageId]: updatedComments
        }
      };
    });
  };

  scrollToBottom = (smooth: boolean = false) => {
    if (this.chatContainerRef.current) {
      const container = this.chatContainerRef.current;
      if (smooth) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      } else {
        container.scrollTop = container.scrollHeight;
      }
    }
  };

  handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ inputMessage: e.target.value });
  };

  generateCloudinaryVideoThumbnail = (videoUrl: string): string => {
    // Convert Cloudinary video URL to thumbnail URL
    // Example: https://res.cloudinary.com/dxdnzv5pt/video/upload/v1767433946/.../file.mp4
    // To: https://res.cloudinary.com/dxdnzv5pt/video/upload/so_0.1/v1767433946/.../file.jpg
    
    if (!videoUrl || !videoUrl.includes('cloudinary.com')) {
      return videoUrl; // Return original if not Cloudinary URL
    }

    try {
      // Replace /video/upload/ with /video/upload/so_0.1/ to seek to 0.1 seconds
      // and change extension from .mp4 to .jpg
      const thumbnailUrl = videoUrl
        .replace('/video/upload/', '/video/upload/so_0.1/')
        .replace(/\.(mp4|mov|avi|webm|mkv)$/i, '.jpg');
      
      return thumbnailUrl;
    } catch (error) {
      console.error('[GroupChat] Failed to generate Cloudinary thumbnail URL:', error);
      return videoUrl; // Fallback to original URL
    }
  };

  generateVideoThumbnail = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      let objectUrl: string | null = null;
      
      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }

      const cleanup = () => {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
        }
        video.src = '';
      };

      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      
      video.onloadedmetadata = () => {
        try {
          video.currentTime = 0.1; // Seek to 0.1 seconds for thumbnail
        } catch (error) {
          // If seeking fails, try 0 seconds
          video.currentTime = 0;
        }
      };

      video.onseeked = () => {
        try {
          canvas.width = video.videoWidth || 320;
          canvas.height = video.videoHeight || 240;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.8);
          cleanup();
          resolve(thumbnailUrl);
        } catch (error) {
          cleanup();
          reject(error);
        }
      };

      video.onerror = (error) => {
        cleanup();
        reject(new Error('Failed to load video'));
      };

      objectUrl = URL.createObjectURL(file);
      video.src = objectUrl;
    });
  };

  handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const maxFiles = 10;
    const maxFileSize = 50 * 1024 * 1024; // 50MB

    if (this.state.selectedFiles.length + files.length > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed`);
      return;
    }

    const validFiles: SelectedFile[] = [];
    const filePromises: Promise<void>[] = [];

    files.forEach((file, index) => {
      if (file.size > maxFileSize) {
        alert(`File ${file.name} exceeds maximum size of 50MB`);
        return;
      }

      const selectedFile: SelectedFile = {
        file,
        preview: undefined
      };

      if (file.type.startsWith('image/')) {
        // For images, use FileReader to create preview
        const reader = new FileReader();
        const promise = new Promise<void>((resolve) => {
          reader.onloadend = () => {
            selectedFile.preview = reader.result as string;
            validFiles.push(selectedFile);
            resolve();
          };
          reader.onerror = () => {
            validFiles.push(selectedFile);
            resolve();
          };
          reader.readAsDataURL(file);
        });
        filePromises.push(promise);
      } else if (file.type.startsWith('video/')) {
        // For videos, generate thumbnail
        const promise = this.generateVideoThumbnail(file)
          .then((thumbnailUrl) => {
            selectedFile.preview = thumbnailUrl;
            validFiles.push(selectedFile);
          })
          .catch((error) => {
            console.error('[GroupChat] Failed to generate video thumbnail:', error);
            // Fallback to object URL if thumbnail generation fails
            selectedFile.preview = URL.createObjectURL(file);
            validFiles.push(selectedFile);
          });
        filePromises.push(promise);
      } else {
        // For other files, just add them
        validFiles.push(selectedFile);
      }
    });

    // Wait for all previews to be generated
    await Promise.all(filePromises);

    this.setState(prevState => ({
      ...prevState,
      selectedFiles: [...prevState.selectedFiles, ...validFiles],
      filePreviews: [...prevState.filePreviews, ...validFiles]
    }));

    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = '';
    }
  };

  removeFile = (index: number) => {
    this.setState(prevState => {
      const newFiles = [...prevState.selectedFiles];
      const newPreviews = [...prevState.filePreviews];
      if (newPreviews[index]?.preview) {
        URL.revokeObjectURL(newPreviews[index].preview);
      }
      newFiles.splice(index, 1);
      newPreviews.splice(index, 1);
      return { 
        ...prevState,
        selectedFiles: newFiles,
        filePreviews: newPreviews
      };
    });
  };

  clearAllFiles = () => {
    this.setState(prevState => {
      prevState.filePreviews.forEach((preview: SelectedFile) => {
        if (preview.preview) {
          URL.revokeObjectURL(preview.preview);
        }
      });
      return {
        ...prevState,
        selectedFiles: [],
        filePreviews: []
      };
    });
  };

  handleGetLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    this.setState({ isGettingLocation: true });

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        // Try to get address from reverse geocoding (optional)
        let address: string | undefined;
        let placeName: string | undefined;
        
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await response.json();
          if (data.display_name) {
            address = data.display_name;
            placeName = data.name || data.display_name.split(',')[0];
          }
        } catch (error) {
          console.log('Could not fetch address:', error);
        }

        this.setState({
          location: { latitude, longitude, address, placeName },
          isGettingLocation: false
        });
      },
      (error) => {
        console.error('Error getting location:', error);
        alert('Failed to get your location. Please try again.');
        this.setState({ isGettingLocation: false });
      }
    );
  };

  removeLocation = () => {
    this.setState({ location: null });
  };

  handleSendMessage = async () => {
    const { inputMessage, replyingTo, selectedFiles, location } = this.state;
    
    // Message must have text, files, or location
    if (!inputMessage.trim() && selectedFiles.length === 0 && !location) return;
    if (!this.props.onSendMessage) return;

    // Create optimistic message
    const tempMessageId = `temp-${Date.now()}`;
    const optimisticAttachments: GroupMessageAttachment[] = [];
    
    selectedFiles.forEach((selectedFile, index) => {
      const file = selectedFile.file;
      const attachmentType = file.type.startsWith('image/') ? 'image' : 
                           file.type.startsWith('video/') ? 'video' : 
                           file.type === 'image/gif' ? 'sticker' : 'file';
      const isVideo = file.type.startsWith('video/');
      const attachment: GroupMessageAttachment = {
        id: `temp-${Date.now()}-${index}`,
        attachmentType: attachmentType as any,
        fileUrl: selectedFile.preview || '',
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        createdAt: new Date().toISOString()
      };
      
      // For videos, use preview as thumbnailUrl
      if (isVideo && selectedFile.preview) {
        attachment.thumbnailUrl = selectedFile.preview;
      }
      
      optimisticAttachments.push(attachment);
    });

    if (location) {
      optimisticAttachments.push({
        id: `temp-location-${Date.now()}`,
        attachmentType: 'location',
        linkUrl: `https://www.google.com/maps?q=${location.latitude},${location.longitude}`,
        metadata: {
          latitude: location.latitude,
          longitude: location.longitude,
          address: location.address,
          placeName: location.placeName
        },
        createdAt: new Date().toISOString()
      });
    }

    const optimisticMessage: GroupMessage = {
      id: tempMessageId,
      senderId: this.props.currentUserProfileId,
      groupId: this.props.group.id,
      messageText: inputMessage || '',
      replyToMessage: replyingTo ? {
        id: replyingTo.id,
        messageText: replyingTo.messageText,
        senderId: replyingTo.senderId,
        senderName: replyingTo.sender?.displayName || 'Unknown',
        senderProfilePic: replyingTo.sender?.profilePic,
        createdAt: replyingTo.createdAt
      } : undefined,
      attachments: optimisticAttachments.length > 0 ? optimisticAttachments : undefined,
      commentsCount: 0,
      positiveSentimentsCount: 0,
      negativeSentimentsCount: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      sender: {
        id: this.props.currentUserProfileId,
        userId: this.props.currentUserId,
        displayName: 'You',
        role: this.props.currentUserRole
      }
    };

    this.addMessage(optimisticMessage);
    this.setState({ isUploading: true });

    try {
      const attachments = selectedFiles.length > 0 ? selectedFiles.map(sf => sf.file) : undefined;
      
      await this.props.onSendMessage(
        this.props.group.id,
        inputMessage || '',
        replyingTo?.id,
        attachments,
        location || undefined
      );

      this.setState(prevState => ({
        ...prevState,
        inputMessage: '',
        replyingTo: null,
        selectedFiles: [],
        filePreviews: [],
        location: null
      }));

      if (this.fileInputRef.current) {
        this.fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('[GroupChat] Failed to send message:', error);
      this.setState(prevState => ({
        messages: prevState.messages.filter(m => m.id !== tempMessageId),
        isUploading: false
      }));
      alert('Failed to send message. Please try again.');
    }
  };

  handleCommentFileSelect = (messageId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const maxFiles = 10;
    const maxFileSize = 50 * 1024 * 1024; // 50MB

    if ((this.state.commentFiles[messageId] || []).length + files.length > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed`);
      return;
    }

    const validFiles: SelectedFile[] = [];

    files.forEach(file => {
      if (file.size > maxFileSize) {
        alert(`File ${file.name} exceeds maximum size of 50MB`);
        return;
      }

      validFiles.push({
        file,
        preview: file.type.startsWith('image/') || file.type.startsWith('video/') 
          ? URL.createObjectURL(file) 
          : undefined
      });
    });

    this.setState(prevState => ({
      commentFiles: {
        ...prevState.commentFiles,
        [messageId]: [...(prevState.commentFiles[messageId] || []), ...validFiles]
      }
    }));

    const input = this.commentFileInputs[messageId]?.current;
    if (input) {
      input.value = '';
    }
  };

  handleCommentGetLocation = (messageId: string) => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        let address: string | undefined;
        let placeName: string | undefined;
        
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await response.json();
          if (data.display_name) {
            address = data.display_name;
            placeName = data.name || data.display_name.split(',')[0];
          }
        } catch (error) {
          console.log('Could not fetch address:', error);
        }

        this.setState(prevState => ({
          commentLocations: {
            ...prevState.commentLocations,
            [messageId]: { latitude, longitude, address, placeName }
          }
        }));
      },
      (error) => {
        console.error('Error getting location:', error);
        alert('Failed to get your location. Please try again.');
      }
    );
  };

  handleSendComment = async (messageId: string) => {
    const commentText = this.state.commentInputs[messageId] || '';
    const commentFiles = this.state.commentFiles[messageId] || [];
    const commentLocation = this.state.commentLocations[messageId];

    if (!commentText.trim() && commentFiles.length === 0 && !commentLocation) return;

    this.setState(prevState => ({
      isUploadingComment: {
        ...prevState.isUploadingComment,
        [messageId]: true
      }
    }));

    try {
      const attachments = commentFiles.length > 0 ? commentFiles.map(sf => sf.file) : undefined;
      
      const response = await apiService.createGroupMessageComment(
        messageId,
        commentText || '',
        undefined, // replyToCommentId - can be added later
        attachments,
        commentLocation || undefined
      );

      if (response.success) {
        // Clear comment input
        this.setState(prevState => {
          const newCommentInputs = { ...prevState.commentInputs };
          delete newCommentInputs[messageId];
          const newCommentFiles = { ...prevState.commentFiles };
          delete newCommentFiles[messageId];
          const newCommentLocations = { ...prevState.commentLocations };
          delete newCommentLocations[messageId];
          
          // Clean up file previews
          (prevState.commentFiles[messageId] || []).forEach((preview: SelectedFile) => {
            if (preview.preview) {
              URL.revokeObjectURL(preview.preview);
            }
          });

          return {
            ...prevState,
            commentInputs: newCommentInputs,
            commentFiles: newCommentFiles,
            commentLocations: newCommentLocations,
            isUploadingComment: {
              ...prevState.isUploadingComment,
              [messageId]: false
            }
          };
        });
      }
    } catch (error) {
      console.error('[GroupChat] Failed to send comment:', error);
      this.setState(prevState => ({
        isUploadingComment: {
          ...prevState.isUploadingComment,
          [messageId]: false
        }
      }));
      alert('Failed to send comment. Please try again.');
    }
  };

  handleToggleComments = (messageId: string) => {
    this.setState(prevState => {
      const expanded = new Set(prevState.expandedComments);
      if (expanded.has(messageId)) {
        expanded.delete(messageId);
      } else {
        expanded.add(messageId);
        // Load comments if not already loaded
        if (!prevState.comments[messageId]) {
          this.loadComments(messageId);
        }
      }
      return { expandedComments: expanded };
    });
  };

  handleToggleSentiment = async (commentId: string, messageId: string, currentSentiment: 'positive' | 'negative' | null) => {
    try {
      if (currentSentiment === 'positive') {
        // Remove positive, add negative
        await apiService.removeCommentSentiment(commentId);
        await apiService.addCommentSentiment(commentId, 'negative');
      } else if (currentSentiment === 'negative') {
        // Remove negative
        await apiService.removeCommentSentiment(commentId);
      } else {
        // Add positive
        await apiService.addCommentSentiment(commentId, 'positive');
      }
    } catch (error) {
      console.error('[GroupChat] Failed to toggle sentiment:', error);
      alert('Failed to update sentiment. Please try again.');
    }
  };

  isOwnMessage = (message: GroupMessage) => {
    return message.senderId === this.props.currentUserProfileId;
  };

  handleForwardClick = (message: GroupMessage) => {
    if (message.id.startsWith('temp-')) return;
    this.setState({ forwardMessage: message });
  };

  handleForwardConfirm = async (params: {
    destinationType: 'dm' | 'group';
    destinationFriendId?: string;
    destinationGroupId?: string;
  }) => {
    const { forwardMessage } = this.state;
    if (!forwardMessage) return;
    await apiService.forwardMessage({
      sourceType: 'group',
      sourceMessageId: forwardMessage.id,
      destinationType: params.destinationType,
      destinationFriendId: params.destinationFriendId,
      destinationGroupId: params.destinationGroupId,
    });
    this.setState({ forwardMessage: null });
  };

  getSenderName = (message: GroupMessage) => {
    if (this.isOwnMessage(message)) {
      return 'You';
    }
    return message.sender?.displayName || 'Unknown';
  };

  getSenderRoleIcon = (role?: string) => {
    if (role === 'admin') {
      return <Crown size={12} style={{ color: '#f59e0b' }} />;
    }
    if (role === 'moderator') {
      return <Shield size={12} style={{ color: '#3b82f6' }} />;
    }
    return null;
  };

  renderAttachment = (attachment: GroupMessageAttachment, messageId?: string): React.ReactNode => {
    const isUploading = messageId?.startsWith('temp-') || 
                       (attachment.fileUrl && attachment.fileUrl.startsWith('data:'));
    
    switch (attachment.attachmentType) {
      case 'image':
        if (!attachment.fileUrl) return null;
        return (
          <div className={styles.messageImage}>
            <div className={styles.imageContainer}>
              <img 
                src={attachment.fileUrl} 
                alt={attachment.fileName || 'Shared image'} 
                loading="lazy"
                style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer' }}
                onClick={() => {
                  if (attachment.fileUrl && !attachment.fileUrl.startsWith('data:')) {
                    window.open(attachment.fileUrl, '_blank');
                  }
                }}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
              {isUploading && (
                <div className={styles.uploadOverlay}>
                  <div className={styles.uploadSpinner}></div>
                  <span className={styles.uploadText}>Uploading...</span>
                  <div className={styles.uploadProgressBar}>
                    <div className={styles.uploadProgressFill}></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      
      case 'video':
        if (!attachment.fileUrl && !attachment.thumbnailUrl) return null;
        
        const videoUrl = attachment.fileUrl && !attachment.fileUrl.startsWith('data:') ? attachment.fileUrl : null;
        
        // Determine thumbnail URL:
        // 1. Use thumbnailUrl from API if available
        // 2. For optimistic messages (data URLs), use the data URL
        // 3. For Cloudinary video URLs, generate thumbnail URL
        // 4. Fallback to null (will show video player)
        let videoThumbnail: string | null = null;
        
        if (attachment.thumbnailUrl) {
          videoThumbnail = attachment.thumbnailUrl;
        } else if (isUploading && attachment.fileUrl?.startsWith('data:')) {
          videoThumbnail = attachment.fileUrl;
        } else if (videoUrl && videoUrl.includes('cloudinary.com')) {
          // Generate Cloudinary thumbnail URL from video URL
          videoThumbnail = this.generateCloudinaryVideoThumbnail(videoUrl);
        }
        
        return (
          <div className={styles.messageVideo}>
            <div className={styles.videoContainer}>
              {videoThumbnail ? (
                <div className={styles.videoThumbnailWrapper}>
                  <img 
                    src={videoThumbnail} 
                    alt={attachment.fileName || 'Video thumbnail'}
                    className={styles.videoThumbnailImage}
                    onError={(e) => {
                      // If thumbnail fails to load, fallback to video player
                      console.error('[GroupChat] Video thumbnail failed to load:', videoThumbnail);
                      const container = e.currentTarget.parentElement?.parentElement;
                      if (container && videoUrl) {
                        // Replace thumbnail with video player
                        const videoElement = document.createElement('video');
                        videoElement.src = videoUrl;
                        videoElement.controls = true;
                        videoElement.className = styles.videoPlayer;
                        videoElement.style.maxWidth = '300px';
                        videoElement.style.maxHeight = '300px';
                        videoElement.style.borderRadius = '8px';
                        container.innerHTML = '';
                        container.appendChild(videoElement);
                      }
                    }}
                  />
                  {videoUrl && (
                    <div 
                      className={styles.videoPlayOverlay}
                      onClick={() => {
                        const videoWindow = window.open('', '_blank');
                        if (videoWindow) {
                          videoWindow.document.write(`
                            <html>
                              <head><title>Video Player</title></head>
                              <body style="margin:0;padding:0;background:#000;display:flex;align-items:center;justify-content:center;height:100vh;">
                                <video controls autoplay style="max-width:100%;max-height:100%;" src="${videoUrl}"></video>
                              </body>
                            </html>
                          `);
                        }
                      }}
                    >
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="white" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}>
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  )}
                </div>
              ) : videoUrl ? (
                <video 
                  src={videoUrl} 
                  controls={!isUploading}
                  preload="metadata"
                  playsInline
                  muted={false}
                  className={styles.videoPlayer}
                >
                  Your browser does not support the video tag.
                </video>
              ) : null}
              {isUploading && (
                <div className={styles.uploadOverlay}>
                  <div className={styles.uploadSpinner}></div>
                  <span className={styles.uploadText}>Uploading...</span>
                  <div className={styles.uploadProgressBar}>
                    <div className={styles.uploadProgressFill}></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      
      case 'file':
      case 'sticker':
        return (
          <div className={styles.messageFile}>
            <Paperclip size={16} />
            <div className={styles.fileInfo}>
              <span>{attachment.fileName || 'File'}</span>
              {attachment.fileSize && (
                <span>{(attachment.fileSize / 1024 / 1024).toFixed(2)} MB</span>
              )}
              {attachment.fileUrl && (
                <a href={attachment.fileUrl} download={attachment.fileName}>
                  <Download size={14} />
                </a>
              )}
            </div>
          </div>
        );
      
      case 'youtube':
        const videoId = attachment.metadata?.videoId;
        const embedUrl = attachment.metadata?.embedUrl || `https://www.youtube.com/embed/${videoId}`;
        return (
          <div className={styles.messageYoutube}>
            {videoId ? (
              <iframe width="100%" height="315" src={embedUrl} frameBorder="0" allowFullScreen />
            ) : (
              <a href={attachment.linkUrl} target="_blank" rel="noopener noreferrer">
                Watch on YouTube
              </a>
            )}
          </div>
        );
      
      case 'facebook':
      case 'twitter':
      case 'instagram':
        return (
          <div className={styles.messageSocial}>
            <a href={attachment.linkUrl} target="_blank" rel="noopener noreferrer">
              Open {attachment.attachmentType.charAt(0).toUpperCase() + attachment.attachmentType.slice(1)}
            </a>
          </div>
        );
      
      case 'link':
        return (
          <div className={styles.messageLink}>
            <a href={attachment.linkUrl} target="_blank" rel="noopener noreferrer">
              {attachment.linkUrl}
            </a>
          </div>
        );
      
      case 'location':
        const { latitude, longitude, address, placeName } = attachment.metadata || {};
        const mapsUrl = attachment.linkUrl || `https://www.google.com/maps?q=${latitude},${longitude}`;
        return (
          <div className={styles.messageLocation}>
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
              <MapPin size={20} />
              <div>
                <span>{placeName || address || 'Shared Location'}</span>
                <span>Open in Google Maps</span>
              </div>
            </a>
          </div>
        );
      
      default:
        return null;
    }
  };

  render() {
    const { group, onClose, members } = this.props;
    const { 
      messages, 
      comments,
      expandedComments,
      inputMessage, 
      replyingTo, 
      selectedFiles,
      filePreviews,
      location,
      isGettingLocation,
      isUploading,
      loading,
      commentInputs,
      commentFiles,
      commentLocations,
      isUploadingComment
    } = this.state;

    const { currentUserRole, onAddMembers } = this.props;
    const isAdmin = currentUserRole === 'admin';

    return (
      <div className={styles.groupChatContainer}>
        <div className={styles.groupChatHeader}>
          <div className={styles.groupInfo}>
            {group.profilePicUrl && (
              <img src={group.profilePicUrl} alt={group.name} className={styles.groupAvatar} />
            )}
            <div>
              <h3>{group.name}</h3>
              {members && (
                <div className={styles.memberCount}>
                  <Users size={14} />
                  <span>{members.length} members</span>
                </div>
              )}
            </div>
          </div>
          <div className={styles.headerActions}>
            {isAdmin && onAddMembers && (
              <button
                type="button"
                className={styles.addMemberButton}
                onClick={onAddMembers}
                title="Add members"
                aria-label="Add members to group"
              >
                <UserPlus size={20} />
                <span>Add member</span>
              </button>
            )}
            <button className={styles.closeButton} onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        <div className={styles.chatMessages} ref={this.chatContainerRef}>
          {loading && messages.length === 0 ? (
            <div className={styles.loadingState}>Loading messages...</div>
          ) : messages.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((message) => {
              const isOwn = this.isOwnMessage(message);
              const messageComments = comments[message.id] || [];
              const isCommentsExpanded = expandedComments.has(message.id);

              return (
                <div key={message.id} id={`message-${message.id}`} className={styles.messageWrapper}>
                  <div className={`${styles.message} ${isOwn ? styles.messageOwn : styles.messageOther}`}>
                    {!isOwn && (
                      <img
                        src={message.sender?.profilePic || 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y&s=200'}
                        alt={this.getSenderName(message)}
                        className={styles.messageAvatar}
                      />
                    )}
                    <div className={styles.messageContent}>
                      {!isOwn && (
                        <div className={styles.messageHeader}>
                          <span className={styles.senderName}>
                            {this.getSenderName(message)}
                            {this.getSenderRoleIcon(message.sender?.role)}
                          </span>
                          <span className={styles.messageTime}>
                            {new Date(message.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      )}
                      {message.replyToMessage && (() => {
                        // Find the original message to get attachments
                        const originalMessage = messages.find(m => m.id === message.replyToMessage!.id);
                        const hasImageOrVideo = originalMessage?.attachments?.some(
                          att => att.attachmentType === 'image' || att.attachmentType === 'video'
                        );
                        const imageOrVideoAttachment = originalMessage?.attachments?.find(
                          att => att.attachmentType === 'image' || att.attachmentType === 'video'
                        );
                        const truncatedText = message.replyToMessage.messageText 
                          ? (message.replyToMessage.messageText.length > 50 
                              ? message.replyToMessage.messageText.substring(0, 50) + '...' 
                              : message.replyToMessage.messageText)
                          : null;

                        return (
                          <div 
                            className={styles.replyPreview}
                            onClick={() => this.handleReplyPreviewClick(message.replyToMessage!.id)}
                            title="Click to view original message"
                          >
                            <Reply size={12} />
                            <div className={styles.replyPreviewContent}>
                              <div className={styles.replySender}>{message.replyToMessage.senderName}</div>
                              {hasImageOrVideo && imageOrVideoAttachment ? (
                                <div className={styles.replyPreviewWithMedia}>
                                  {imageOrVideoAttachment.attachmentType === 'image' ? (
                                    <img 
                                      src={imageOrVideoAttachment.thumbnailUrl || imageOrVideoAttachment.fileUrl || ''} 
                                      alt="Reply preview" 
                                      className={styles.replyPreviewImage}
                                    />
                                  ) : imageOrVideoAttachment.attachmentType === 'video' ? (
                                    <div className={styles.replyPreviewVideo}>
                                      <img 
                                        src={imageOrVideoAttachment.thumbnailUrl || this.generateCloudinaryVideoThumbnail(imageOrVideoAttachment.fileUrl || '')} 
                                        alt="Video thumbnail" 
                                        className={styles.replyPreviewVideoThumbnail}
                                        onError={(e) => {
                                          // If thumbnail fails to load, generate one from the video
                                          const imgElement = e.currentTarget;
                                          const videoUrl = imageOrVideoAttachment.fileUrl;
                                          
                                          if (!videoUrl || videoUrl.startsWith('data:')) {
                                            // Can't generate thumbnail from data URL or missing URL
                                            return;
                                          }
                                          
                                          // Generate thumbnail from video
                                          const video = document.createElement('video');
                                          const canvas = document.createElement('canvas');
                                          const ctx = canvas.getContext('2d');
                                          
                                          if (!ctx) return;
                                          
                                          video.preload = 'metadata';
                                          video.muted = true;
                                          video.playsInline = true;
                                          video.crossOrigin = 'anonymous';
                                          
                                          video.onloadedmetadata = () => {
                                            try {
                                              video.currentTime = 0.1; // Seek to 0.1 seconds for thumbnail
                                            } catch (error) {
                                              video.currentTime = 0;
                                            }
                                          };
                                          
                                          video.onseeked = () => {
                                            try {
                                              canvas.width = video.videoWidth || 320;
                                              canvas.height = video.videoHeight || 240;
                                              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                                              const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.8);
                                              imgElement.src = thumbnailUrl;
                                              video.src = '';
                                            } catch (error) {
                                              console.error('[GroupChat] Failed to generate video thumbnail:', error);
                                            }
                                          };
                                          
                                          video.onerror = () => {
                                            video.src = '';
                                          };
                                          
                                          video.src = videoUrl;
                                        }}
                                      />
                                      <div className={styles.replyPreviewPlayIcon}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                                          <path d="M8 5v14l11-7z"/>
                                        </svg>
                                      </div>
                                    </div>
                                  ) : null}
                                  {truncatedText && (
                                    <span className={styles.replyText}>{truncatedText}</span>
                                  )}
                                </div>
                              ) : truncatedText ? (
                                <div className={styles.replyText}>{truncatedText}</div>
                              ) : (
                                <div className={styles.replyText} style={{ fontStyle: 'italic' }}>Attachment</div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                      {message.messageText && (
                        <div className={styles.messageText}>{message.messageText}</div>
                      )}
                      {message.attachments && message.attachments.length > 0 && (
                        <div className={styles.attachmentsContainer}>
                          {message.attachments.map((attachment) => (
                            <div key={attachment.id}>
                              {this.renderAttachment(attachment, message.id)}
                            </div>
                          ))}
                        </div>
                      )}
                      {isOwn && (
                        <span className={styles.messageTime}>
                          {new Date(message.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                      
                      {/* Comment and Sentiment Actions */}
                      <div className={styles.messageActions}>
                        <button 
                          className={styles.actionButton}
                          onClick={() => this.handleReplyClick(message)}
                          title="Reply"
                        >
                          <Reply size={16} />
                        </button>
                        {!message.id.startsWith('temp-') && (
                          <button 
                            className={styles.actionButton}
                            onClick={() => this.handleForwardClick(message)}
                            title="Forward"
                          >
                            <Forward size={16} />
                          </button>
                        )}
                        <button 
                          className={styles.actionButton}
                          onClick={() => this.handleToggleComments(message.id)}
                        >
                          <MessageSquare size={16} />
                          <span>{message.commentsCount || 0}</span>
                        </button>
                        <button className={styles.actionButton}>
                          <ThumbsUp size={16} />
                          <span>{message.positiveSentimentsCount || 0}</span>
                        </button>
                        <button className={styles.actionButton}>
                          <ThumbsDown size={16} />
                          <span>{message.negativeSentimentsCount || 0}</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Comments Section */}
                  {isCommentsExpanded && (
                    <div className={styles.commentsSection}>
                      {messageComments.map((comment) => (
                        <div key={comment.id} className={styles.comment}>
                          <img
                            src={comment.commenter?.profilePic || 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y&s=200'}
                            alt={comment.commenter?.displayName || 'Unknown'}
                            className={styles.commentAvatar}
                          />
                          <div className={styles.commentContent}>
                            <div className={styles.commentHeader}>
                              <span>{comment.commenter?.displayName || 'Unknown'}</span>
                              <span>{new Date(comment.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                            {comment.commentText && (
                              <div className={styles.commentText}>{comment.commentText}</div>
                            )}
                            {comment.attachments && comment.attachments.length > 0 && (
                              <div>
                                {comment.attachments.map((attachment) => (
                                  <div key={attachment.id}>
                                    {this.renderAttachment(attachment)}
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className={styles.commentActions}>
                              <button
                                className={`${styles.sentimentButton} ${comment.userSentiment === 'positive' ? styles.active : ''}`}
                                onClick={() => this.handleToggleSentiment(comment.id, message.id, comment.userSentiment === 'positive' ? 'positive' : null)}
                              >
                                <ThumbsUp size={14} />
                                <span>{comment.positiveSentimentsCount || 0}</span>
                              </button>
                              <button
                                className={`${styles.sentimentButton} ${comment.userSentiment === 'negative' ? styles.active : ''}`}
                                onClick={() => this.handleToggleSentiment(comment.id, message.id, comment.userSentiment === 'negative' ? 'negative' : null)}
                              >
                                <ThumbsDown size={14} />
                                <span>{comment.negativeSentimentsCount || 0}</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Comment Input */}
                      <div className={styles.commentInput}>
                        <input
                          type="text"
                          value={commentInputs[message.id] || ''}
                          onChange={(e) => this.setState(prevState => ({
                            commentInputs: { ...prevState.commentInputs, [message.id]: e.target.value }
                          }))}
                          placeholder="Write a comment..."
                          className={styles.commentTextInput}
                        />
                        <button
                          className={styles.attachCommentButton}
                          onClick={() => this.commentFileInputs[message.id]?.current?.click()}
                        >
                          <Paperclip size={16} />
                        </button>
                        <input
                          type="file"
                          ref={this.commentFileInputs[message.id] || (this.commentFileInputs[message.id] = React.createRef())}
                          multiple
                          onChange={(e) => this.handleCommentFileSelect(message.id, e)}
                          style={{ display: 'none' }}
                          accept="image/*,video/*,application/*"
                        />
                        <button
                          className={styles.locationCommentButton}
                          onClick={() => this.handleCommentGetLocation(message.id)}
                        >
                          <MapPin size={16} />
                        </button>
                        <button
                          className={styles.sendCommentButton}
                          onClick={() => this.handleSendComment(message.id)}
                          disabled={isUploadingComment[message.id] || (!commentInputs[message.id]?.trim() && !commentFiles[message.id]?.length && !commentLocations[message.id])}
                        >
                          {isUploadingComment[message.id] ? <Loader size={16} /> : <Send size={16} />}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {replyingTo && (() => {
          const hasImageOrVideo = replyingTo.attachments?.some(
            att => att.attachmentType === 'image' || att.attachmentType === 'video'
          );
          const imageOrVideoAttachment = replyingTo.attachments?.find(
            att => att.attachmentType === 'image' || att.attachmentType === 'video'
          );
          const truncatedText = replyingTo.messageText 
            ? (replyingTo.messageText.length > 50 
                ? replyingTo.messageText.substring(0, 50) + '...' 
                : replyingTo.messageText)
            : null;

          return (
            <div className={styles.replyBar}>
              <div className={styles.replyInfo}>
                <Reply size={14} />
                <div className={styles.replyInfoContent}>
                  <span className={styles.replyLabel}>Replying to</span>
                  <div className={styles.replyName}>
                    {replyingTo.sender?.displayName || 'Unknown'}
                  </div>
                  {hasImageOrVideo && imageOrVideoAttachment ? (
                    <div className={styles.replyBarWithMedia}>
                      {imageOrVideoAttachment.attachmentType === 'image' ? (
                        <img 
                          src={imageOrVideoAttachment.thumbnailUrl || imageOrVideoAttachment.fileUrl || ''} 
                          alt="Reply preview" 
                          className={styles.replyBarImage}
                        />
                      ) : imageOrVideoAttachment.attachmentType === 'video' ? (
                        <div className={styles.replyBarVideo}>
                          <img 
                            src={imageOrVideoAttachment.thumbnailUrl || this.generateCloudinaryVideoThumbnail(imageOrVideoAttachment.fileUrl || '')} 
                            alt="Video thumbnail" 
                            className={styles.replyBarVideoThumbnail}
                            onError={(e) => {
                              // If thumbnail fails to load, generate one from the video
                              const imgElement = e.currentTarget;
                              const videoUrl = imageOrVideoAttachment.fileUrl;
                              
                              if (!videoUrl || videoUrl.startsWith('data:')) {
                                // Can't generate thumbnail from data URL or missing URL
                                return;
                              }
                              
                              // Generate thumbnail from video
                              const video = document.createElement('video');
                              const canvas = document.createElement('canvas');
                              const ctx = canvas.getContext('2d');
                              
                              if (!ctx) return;
                              
                              video.preload = 'metadata';
                              video.muted = true;
                              video.playsInline = true;
                              video.crossOrigin = 'anonymous';
                              
                              video.onloadedmetadata = () => {
                                try {
                                  video.currentTime = 0.1; // Seek to 0.1 seconds for thumbnail
                                } catch (error) {
                                  video.currentTime = 0;
                                }
                              };
                              
                              video.onseeked = () => {
                                try {
                                  canvas.width = video.videoWidth || 320;
                                  canvas.height = video.videoHeight || 240;
                                  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                                  const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.8);
                                  imgElement.src = thumbnailUrl;
                                  video.src = '';
                                } catch (error) {
                                  console.error('[GroupChat] Failed to generate video thumbnail:', error);
                                }
                              };
                              
                              video.onerror = () => {
                                video.src = '';
                              };
                              
                              video.src = videoUrl;
                            }}
                          />
                          <div className={styles.replyBarPlayIcon}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </div>
                        </div>
                      ) : null}
                      {truncatedText && (
                        <span className={styles.replyBarText}>{truncatedText}</span>
                      )}
                    </div>
                  ) : truncatedText ? (
                    <div className={styles.replyBarText}>{truncatedText}</div>
                  ) : replyingTo.attachments && replyingTo.attachments.length > 0 ? (
                    <div className={styles.replyBarText} style={{ fontStyle: 'italic' }}>Attachment</div>
                  ) : (
                    <div className={styles.replyBarText} style={{ fontStyle: 'italic' }}>Message</div>
                  )}
                </div>
              </div>
              <button 
                onClick={this.handleCancelReply}
                className={styles.cancelReplyButton}
                title="Cancel reply"
              >
                <X size={16} />
              </button>
            </div>
          );
        })()}

        {selectedFiles.length > 0 && (
          <div className={styles.filePreviews}>
            {selectedFiles.map((selectedFile, index) => {
              const preview = filePreviews[index]?.preview;
              const isVideo = selectedFile.file.type.startsWith('video/');
              const isImage = selectedFile.file.type.startsWith('image/');
              
              return (
                <div key={index} className={styles.filePreview}>
                  {preview && (isImage || isVideo) ? (
                    <div className={styles.previewContainer}>
                      {isVideo ? (
                        <div className={styles.videoThumbnailContainer}>
                          <img src={preview} alt="Video thumbnail" className={styles.videoThumbnail} />
                          <div className={styles.videoPlayIcon}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </div>
                        </div>
                      ) : (
                        <img src={preview} alt="Preview" />
                      )}
                    </div>
                  ) : (
                    <div className={styles.fileIconPreview}>
                      <Paperclip size={24} />
                      <span className={styles.fileNamePreview}>{selectedFile.file.name}</span>
                    </div>
                  )}
                  <button 
                    className={styles.removeFileButton}
                    onClick={() => this.removeFile(index)}
                    title="Remove file"
                  >
                    <X size={16} />
                  </button>
                </div>
              );
            })}
            <button className={styles.clearAllButton} onClick={this.clearAllFiles}>
              Clear All
            </button>
          </div>
        )}

        {location && (
          <div className={styles.locationPreview}>
            <MapPin size={16} />
            <div>
              <span>{location.placeName || location.address || 'Location'}</span>
              <span>Open in Maps</span>
            </div>
            <button onClick={this.removeLocation}>
              <X size={16} />
            </button>
          </div>
        )}

        <div className={styles.chatInput}>
          <button
            className={styles.attachButton}
            onClick={() => this.fileInputRef.current?.click()}
            title="Attach file"
          >
            <Paperclip size={20} />
          </button>
          <input
            type="file"
            ref={this.fileInputRef}
            multiple
            onChange={this.handleFileSelect}
            style={{ display: 'none' }}
            accept="image/*,video/*,application/*"
          />
          <button
            className={styles.locationButton}
            onClick={this.handleGetLocation}
            disabled={isGettingLocation}
            title="Share location"
          >
            {isGettingLocation ? <Loader size={20} /> : <MapPin size={20} />}
          </button>
          <input
            type="text"
            value={inputMessage}
            onChange={this.handleInputChange}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this.handleSendMessage();
              }
            }}
            placeholder="Type a message..."
            className={styles.messageInput}
          />
          <button
            className={`${styles.sendButton} ${isUploading ? styles.sendButtonUploading : ''}`}
            onClick={this.handleSendMessage}
            disabled={isUploading || (!inputMessage.trim() && selectedFiles.length === 0 && !location)}
          >
            {isUploading ? (
              <div className={styles.sendButtonSpinner}>
                <div className={styles.spinnerCircle}></div>
                <span className={styles.sendButtonText}>Sending...</span>
              </div>
            ) : (
              <Send size={20} />
            )}
          </button>
        </div>

        {this.state.forwardMessage && (
          <ForwardDestinationModal
            visible={!!this.state.forwardMessage}
            onClose={() => this.setState({ forwardMessage: null })}
            sourceType="group"
            sourceMessageId={this.state.forwardMessage.id}
            excludeGroupId={this.props.group.id}
            onForward={this.handleForwardConfirm}
          />
        )}
      </div>
    );
  }
}

export default GroupChat;
